import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Loader2,
  RefreshCcw,
  Copy,
  CheckCircle2,
  XCircle,
  CreditCard,
  Receipt,
  FileText,
  Banknote,
  Landmark,
  Smartphone,
  Save,
  CheckSquare,
  Building2,
  Bug,
} from 'lucide-react';

import { CONFIG } from '../config/config';
import { useSocket } from '../context/SocketContext';
import { useUser } from '../context/UserContext';
import { mapDbPatient } from '../utils/patientHelpers';
import { getDisplayMusiqId } from '../utils/musiqId';
import { calcCotation } from '../utils/calcCotation';
import {
  sheetSyncAndPoll,
  sheetGet,
  normalizeCodes as normalizeCodesApi,
  normalizeSheetPayload,
  domainOf,
} from '../api/cotations';

import PageLayout from '../components/PageLayout';
import CotationOverlay from '../components/CotationOverlay';
import QUALIFICATION_CONFIG from '../config/qualification.json';

const API_URL = (CONFIG.API_URL || '/api').replace(/\/+$/, '');
const PROFILES_DATA = QUALIFICATION_CONFIG.profiles || QUALIFICATION_CONFIG;
const FIELD_DEFS = QUALIFICATION_CONFIG.fieldDefinitions || {};

function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
}

function isCotationLibre(p) {
  const t = (p?.cotationLibre ?? p?.cotation_libre ?? '').trim();
  return t.length > 0;
}

function getCotationLibreText(p) {
  return String(p?.cotationLibre ?? p?.cotation_libre ?? '').trim();
}

function getCodesStringFromPatient(p) {
  if (!p) return '';
  const direct = p?.codes || p?.cotationCodes || p?.cotation_codes || p?.cotation_json?.codes || '';
  if (direct) {
    return String(direct).toUpperCase().replace(/\s*\+\s*/g, '+');
  }

  try {
    const raw = localStorage.getItem(`musiq:cotation:${p.id}`);
    if (raw) {
      const j = JSON.parse(raw);
      if (j?.codes) {
        return String(j.codes).toUpperCase().replace(/\s*\+\s*/g, '+');
      }
    }
  } catch {}

  try {
    const { countedActes, appliedMajos } = calcCotation({
      domaine: p?.domaine || p?.cotationDomaine || undefined,
      actes: Array.isArray(p?.actes) ? p.actes : [],
      majorations: Array.isArray(p?.majorations) ? p.majorations : [],
    });
    const arr = [
      ...countedActes.map((a) => a.code),
      ...(Array.isArray(appliedMajos) ? appliedMajos : [appliedMajos]).filter(Boolean),
    ];
    return arr.join('+').toUpperCase();
  } catch {}
  return '';
}

function normalizeCodesLocal(codes) {
  return String(codes || '')
    .toUpperCase()
    .replace(/"/g, '')
    .replace(/\s*\+\s*/g, '+')
    .replace(/\+{2,}/g, '+')
    .replace(/^\+|\+$/g, '');
}

async function copyToClipboard(text) {
  const str = String(text ?? '').trim();
  if (!str) return false;

  if (navigator?.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(str);
      return true;
    } catch {}
  }

  try {
    const textArea = document.createElement('textarea');
    textArea.value = str;
    textArea.style.position = 'fixed';
    textArea.style.top = '-9999px';
    textArea.style.left = '-9999px';
    textArea.setAttribute('readonly', '');
    document.body.appendChild(textArea);
    textArea.select();
    textArea.setSelectionRange(0, 99999);
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  } catch (err) {
    console.error('Échec copie :', err);
    return false;
  }
}

function parseNum(val) {
  if (val === null || val === undefined) return 0;
  const str = String(val).replace(/[^\d.,-]/g, '').replace(',', '.').trim();
  const parsed = parseFloat(str);
  return isNaN(parsed) ? 0 : parsed;
}

function renderTypeFactBadge(typeFact) {
  if (!typeFact) return <span className="text-sm font-medium text-slate-500">—</span>;

  switch (typeFact) {
    case 'FSE':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
          <CreditCard className="h-3.5 w-3.5 text-emerald-600" />
          FSE
        </span>
      );
    case 'FSP':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
          <FileText className="h-3.5 w-3.5 text-amber-600" />
          FSP
        </span>
      );
    case 'NoteHono':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold bg-cyan-50 text-cyan-800 border border-cyan-200">
          <Receipt className="h-3.5 w-3.5 text-cyan-600" />
          NoteHono
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
          <FileText className="h-3.5 w-3.5 text-slate-600" />
          {typeFact}
        </span>
      );
  }
}

function QualificationPanel({
  onQualificationChange,
  onClearChiffrage,
  filterTP,
  setFilterTP,
  etablissement,
}) {
  const [activeKey, setActiveKey] = useState('ss_pp100_nrm');
  const [showSecondary, setShowSecondary] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [infoConfirmed, setInfoConfirmed] = useState(false);
  const [filterSS, setFilterSS] = useState(true);

  // Détection dynamique du trigramme avec fallbacks
  const trigramme = useMemo(() => {
    const direct = etablissement?.trigramme || etablissement?.code;
    if (direct) return String(direct).trim().toLowerCase();
    try {
      const stored = JSON.parse(localStorage.getItem('etablissement') || sessionStorage.getItem('etablissement') || '{}');
      if (stored?.trigramme) return String(stored.trigramme).trim().toLowerCase();
    } catch {}
    return '';
  }, [etablissement]);

  const uniqueTPList = useMemo(() => {
    if (Array.isArray(FIELD_DEFS?.specialTP?.options)) {
      return FIELD_DEFS.specialTP.options;
    }
    const list = Object.values(PROFILES_DATA).flatMap((p) => p.specialTP);
    return [...new Set(list)];
  }, []);

  const activeProfile = PROFILES_DATA[activeKey];
  const firstSecondaryKey = Object.keys(PROFILES_DATA).find((k) =>
    PROFILES_DATA[k].disp.startsWith('Secondary')
  );

  // Remplacement dynamique de xxx par le trigramme du site
  const formattedInfosCpl = useMemo(() => {
    const raw = activeProfile?.infosCpl;
    if (!raw) return '';
    const site = trigramme || 'xxx';
    return raw
      .replace(/xxx/gi, site)
      .replace(/\s*\(xxx\s*=\s*site\)/gi, '')
      .replace(/Ã©/g, 'é')
      .replace(/Ã /g, 'à')
      .replace(/Ã¨/g, 'è');
  }, [activeProfile?.infosCpl, trigramme]);

  const resQualif = useMemo(() => {
    if (!activeProfile) return { isInvalid: true, requiresCheck: false, infoConfirmed: false };
    return {
      profileKey: activeKey,
      sS: filterSS,
      specialTP: filterTP,
      profileSpecialTPList: Array.isArray(activeProfile.specialTP) ? activeProfile.specialTP : [],
      dept: activeProfile.dept,
      pctAppDpt: activeProfile.pctAppDpt,
      pmtBRSS: activeProfile.pmtBRSS,
      pmtDpt: activeProfile.pmtDpt,
      typeFact: activeProfile.typeFact,
      tp4Partner: activeProfile.tp4Partner,
      infosCpl: formattedInfosCpl,
      infoConfirmed,
      isInvalid,
      requiresCheck: Boolean(activeProfile.infosCpl),
    };
  }, [activeKey, activeProfile, filterSS, filterTP, formattedInfosCpl, infoConfirmed, isInvalid]);

  useEffect(() => {
    if (onQualificationChange) {
      onQualificationChange(resQualif);
    }
  }, [resQualif, onQualificationChange]);

  const selectMain = (key, updateFilters = true) => {
    setIsInvalid(false);
    setInfoConfirmed(false);
    onClearChiffrage?.();
    if (key === 'autre') {
      setShowSecondary(true);
      if (firstSecondaryKey) selectSecondary(firstSecondaryKey, updateFilters, false);
    } else {
      setShowSecondary(false);
      setActiveKey(key);
      if (updateFilters) syncFiltersWithProfile(PROFILES_DATA[key]);
    }
  };

  const selectSecondary = (key, updateFilters = true, shouldClear = true) => {
    setIsInvalid(false);
    setInfoConfirmed(false);
    if (shouldClear) onClearChiffrage?.();
    setActiveKey(key);
    if (updateFilters) syncFiltersWithProfile(PROFILES_DATA[key]);
  };

  const syncFiltersWithProfile = (profile) => {
    if (!profile) return;
    if (!profile.sS.includes(filterSS)) setFilterSS(profile.sS[0]);
    if (profile.specialTP.length > 0 && !profile.specialTP.includes(filterTP)) {
      setFilterTP(profile.specialTP[0]);
    }
  };

  const onFilterChange = (newSS, newTP, shouldClear = true) => {
    setFilterSS(newSS);
    setFilterTP(newTP);
    setInfoConfirmed(false);
    if (shouldClear) onClearChiffrage?.();

    let matchedKey = null;
    for (const key in PROFILES_DATA) {
      const p = PROFILES_DATA[key];
      if (p.sS.includes(newSS) && p.specialTP.includes(newTP)) {
        matchedKey = key;
        break;
      }
    }

    if (matchedKey) {
      setIsInvalid(false);
      const profile = PROFILES_DATA[matchedKey];
      if (profile.disp.startsWith('Main')) {
        setShowSecondary(false);
        setActiveKey(matchedKey);
      } else {
        setShowSecondary(true);
        setActiveKey(matchedKey);
      }
    } else {
      setActiveKey('');
      setShowSecondary(false);
      setIsInvalid(true);
    }
  };

  const secondaryProfiles = Object.entries(PROFILES_DATA).filter(([_, p]) =>
    p.disp.startsWith('Secondary')
  );

  return (
    <div
      className={`rounded-2xl border bg-white p-4 sm:p-5 shadow-sm transition-all border-l-4 border-l-[#B3000A] space-y-4 ${
        isInvalid ? 'border-amber-300' : 'border-slate-200'
      }`}
    >
      <div className="flex items-center justify-between pb-2 border-b border-slate-100">
        <h2 className="text-base sm:text-lg font-semibold text-slate-800">
          Qualification du passage
        </h2>
      </div>

      <div className="space-y-2.5">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          {Object.entries(PROFILES_DATA)
            .filter(([_, p]) => p.disp.startsWith('Main'))
            .map(([k, p]) => (
              <button
                key={k}
                type="button"
                onClick={() => selectMain(k, true)}
                className={`min-h-[56px] p-2.5 rounded-xl border text-xs sm:text-[13px] font-semibold text-center flex items-center justify-center transition-all ${
                  !showSecondary && activeKey === k
                    ? 'bg-gradient-to-r from-[#B3000A] to-[#E1141C] text-white border-transparent shadow-sm'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-red-200 hover:bg-red-50/40 hover:text-[#B3000A]'
                }`}
              >
                {p.desc}
              </button>
            ))}

          <button
            type="button"
            onClick={() => selectMain('autre', true)}
            className={`min-h-[56px] p-2.5 rounded-xl border text-xs sm:text-[13px] font-semibold text-center flex items-center justify-center transition-all ${
              showSecondary
                ? 'bg-gradient-to-r from-[#B3000A] to-[#E1141C] text-white border-transparent shadow-sm'
                : 'bg-white text-slate-700 border-slate-200 hover:border-red-200 hover:bg-red-50/40 hover:text-[#B3000A]'
            }`}
          >
            Autre
          </button>
        </div>

        {showSecondary && (
          <div className="space-y-2 pt-2 border-t border-slate-100">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {secondaryProfiles.slice(0, 3).map(([k, p]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => selectSecondary(k, true)}
                  className={`min-h-[52px] p-2 rounded-lg border text-xs font-semibold text-center flex items-center justify-center transition-all ${
                    activeKey === k
                      ? 'bg-rose-100 text-rose-950 border-rose-300 ring-1 ring-rose-300 shadow-sm'
                      : 'bg-rose-50/60 text-rose-900 border-rose-200/80 hover:bg-rose-100/70 hover:border-rose-300'
                  }`}
                >
                  {p.desc}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {secondaryProfiles.slice(3).map(([k, p]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => selectSecondary(k, true)}
                  className={`min-h-[52px] p-2 rounded-lg border text-xs font-semibold text-center flex items-center justify-center transition-all ${
                    activeKey === k
                      ? 'bg-rose-100 text-rose-950 border-rose-300 ring-1 ring-rose-300 shadow-sm'
                      : 'bg-rose-50/60 text-rose-900 border-rose-200/80 hover:bg-rose-100/70 hover:border-rose-300'
                  }`}
                >
                  {p.desc}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {isInvalid && (
        <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-xs sm:text-sm font-medium">
          Combinaison non reconnue / invalide
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3.5 sm:p-4 text-sm grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase text-slate-500 mb-1.5">
            {FIELD_DEFS?.sS?.label || 'Détient N° de sécurité sociale français'}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onFilterChange(!filterSS, filterTP, true)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                filterSS ? 'bg-emerald-600' : 'bg-slate-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  filterSS ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span
              className={`text-xs font-bold ${
                filterSS ? 'text-emerald-700' : 'text-slate-500'
              }`}
            >
              {filterSS ? 'Oui' : 'Non'}
            </span>
          </div>
        </div>

        <div className="flex flex-col">
          <label className="text-[11px] font-semibold uppercase text-slate-500 mb-1">
            {FIELD_DEFS?.specialTP?.label || 'Lien contractuel spécial'}
          </label>
          <select
            value={filterTP}
            onChange={(e) => onFilterChange(filterSS, e.target.value, true)}
            className="h-9 w-full rounded-lg border border-slate-300 bg-white px-3 text-xs sm:text-sm font-medium text-slate-800 focus:border-[#B3000A] focus:ring-1 focus:ring-[#B3000A] focus:outline-none"
          >
            {uniqueTPList.map((tp) => (
              <option key={tp} value={tp}>
                {tp}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 pt-1 border-t border-slate-200/60 md:border-t-0">
          <span className="text-[11px] font-semibold uppercase text-slate-500">
            {FIELD_DEFS?.dept?.label || 'MUS facture le dépassement ? :'}
          </span>
          {activeProfile ? (
            activeProfile.dept ? (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                Oui
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                <XCircle className="h-3.5 w-3.5 text-rose-600" />
                Non
              </span>
            )
          ) : (
            <span className="text-sm font-medium text-slate-500">—</span>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1 border-t border-slate-200/60 md:border-t-0">
          <span className="text-[11px] font-semibold uppercase text-slate-500">
            {FIELD_DEFS?.typeFact?.label || 'Type de facturation :'}
          </span>
          {renderTypeFactBadge(activeProfile?.typeFact)}
        </div>

        <div className="md:col-span-2 flex items-start gap-2 pt-1 border-t border-slate-200/60">
          <span className="text-[11px] font-semibold uppercase text-slate-500 shrink-0 mt-0.5">
            {FIELD_DEFS?.ex?.label || 'Exemple :'}
          </span>
          <span className="text-xs sm:text-sm text-slate-700 font-medium">
            {activeProfile?.ex || '—'}
          </span>
        </div>
      </div>

      {formattedInfosCpl && (
        <label className="flex items-center gap-3 p-3.5 sm:p-4 rounded-xl border border-[#FDE68A] bg-[#FEF9C3]/70 text-[#713F12] text-xs sm:text-sm font-medium cursor-pointer select-none transition-colors hover:bg-[#FEF08A]/70">
          <input
            type="checkbox"
            checked={infoConfirmed}
            onChange={(e) => setInfoConfirmed(e.target.checked)}
            className="h-5 w-5 rounded border-slate-400 text-[#B3000A] focus:ring-[#B3000A] cursor-pointer"
          />
          <span className="leading-snug">{formattedInfosCpl}</span>
        </label>
      )}
    </div>
  );
}

export default function SortiePatient({ patients = [], setPatients = () => {} }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const socket = useSocket();
  const { etablissement } = useUser();
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const [sp, setSearchParams] = useSearchParams();

  const [patient, setPatient] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [sheetMap, setSheetMap] = useState({});
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetLoadingSince, setSheetLoadingSince] = useState(null);

  const [codesMap, setCodesMap] = useState({});
  const [qualificationData, setQualificationData] = useState(null);

  // Configuration dynamique des tiers reçue du backend
  const [tiersMapping, setTiersMapping] = useState({});

  const [filterTP, setFilterTP] = useState('Aucun');
  const [selectedSpecialTP, setSelectedSpecialTP] = useState('');

  const [payments, setPayments] = useState({
    tpe: '',
    especes: '',
    virement: '',
    cheque: '',
    sumup: '',
  });
  const [showVirementCheque, setShowVirementCheque] = useState(false);
  const [virementManagerConfirmed, setVirementManagerConfirmed] = useState(false);
  const [isSavingPayment, setIsSavingPayment] = useState(false);

  // Panneau debug administrateur
  const [isDebugOpen, setIsDebugOpen] = useState(false);

  // Récupération du rôle utilisateur et test du statut admin
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  })();

  const role =
    storedUser?.role ||
    localStorage.getItem('role') ||
    sessionStorage.getItem('role') ||
    parseJwt(token)?.role ||
    '';

  const isAdmin = String(role).toLowerCase() === 'admin';

  const overlayId = sp.get('cotation');

  const stepOf = (p) => Number(p?.currentStep ?? p?.currentstep ?? p?.currentstep ?? NaN);
  const getUid = (p) => getDisplayMusiqId(p);
  const fmtDT = (raw) =>
    raw
      ? new Date(raw).toLocaleString('fr-FR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';

  const parsePayment = (val) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? 0 : parsed;
  };

  const totalCollected =
    parsePayment(payments.tpe) +
    parsePayment(payments.especes) +
    (virementManagerConfirmed ? parsePayment(payments.virement) + parsePayment(payments.cheque) : 0) +
    parsePayment(payments.sumup);

  const isFetchDisabled = useMemo(() => {
    if (!qualificationData) return true;
    if (qualificationData.isInvalid) return true;
    if (qualificationData.requiresCheck && !qualificationData.infoConfirmed) return true;
    return false;
  }, [qualificationData]);

  const sheet = useMemo(() => (patient?.id ? sheetMap[patient.id]?.table || [] : []), [sheetMap, patient?.id]);
  const rows = useMemo(() => (Array.isArray(sheet) ? sheet : []), [sheet]);

  const isNoteHono = String(qualificationData?.typeFact || '').trim().toLowerCase() === 'notehono';

  // Statuts de paiement
  const isSSMut = (val) => {
    const v = String(val ?? '').trim().toLowerCase();
    return v === 'ssmut' || v === 'ss + mutuelle';
  };
  const isPatient = (val) => String(val ?? '').trim().toLowerCase() === 'patient';
  const isPersonne = (val) => String(val ?? '').trim().toLowerCase() === 'personne';

  // CALCULS AUTOMATIQUES
  // - si Personne : ne rien faire
  // - si Patient : montant à encaisser
  // - sinon (dont SSMut et tiers) : montant dans tiers payant
  const { aEncaisser, tiersPayant, hasChiffrage, brssTotalCalc, depassementTotalCalc } = useMemo(() => {
    if (!rows || rows.length === 0 || !qualificationData) {
      return { aEncaisser: 0, tiersPayant: 0, hasChiffrage: false, brssTotalCalc: 0, depassementTotalCalc: 0 };
    }

    let brssTotal = 0;
    let montantTotal = 0;
    let depassementTotal = 0;

    const totalRow = rows.slice(1).find((r) => {
      const code = Array.isArray(r) ? r[0] : (r && typeof r === 'object' ? Object.values(r)[0] : '');
      return /^total/i.test(String(code ?? ''));
    });

    if (isNoteHono) {
      if (totalRow) {
        const val = Array.isArray(totalRow) ? totalRow[1] : Object.values(totalRow)[1];
        montantTotal = parseNum(val);
      } else {
        montantTotal = rows.slice(1).reduce((sum, r) => {
          const val = Array.isArray(r) ? r[1] : (r && typeof r === 'object' ? Object.values(r)[1] : 0);
          return sum + parseNum(val);
        }, 0);
      }
      depassementTotal = montantTotal;
    } else {
      if (totalRow) {
        const val2 = Array.isArray(totalRow) ? totalRow[2] : Object.values(totalRow)[2];
        const val3 = Array.isArray(totalRow) ? totalRow[3] : Object.values(totalRow)[3];
        montantTotal = parseNum(val2);
        brssTotal = parseNum(val3);
      } else {
        rows.slice(1).forEach((r) => {
          const val2 = Array.isArray(r) ? r[2] : (r && typeof r === 'object' ? Object.values(r)[2] : 0);
          const val3 = Array.isArray(r) ? r[3] : (r && typeof r === 'object' ? Object.values(r)[3] : 0);
          montantTotal += parseNum(val2);
          brssTotal += parseNum(val3);
        });
      }
      depassementTotal = Math.max(0, montantTotal - brssTotal);
    }

    let encaisisserCalc = 0;
    let tpCalc = 0;

    const dispatch = (valeurConfig, montant) => {
      if (isPersonne(valeurConfig)) {
        return;
      }
      if (isPatient(valeurConfig)) {
        encaisisserCalc += montant;
      } else {
        tpCalc += montant;
      }
    };

    if (isNoteHono) {
      dispatch(qualificationData.pmtDpt, montantTotal);
    } else {
      dispatch(qualificationData.pmtBRSS, brssTotal);
      dispatch(qualificationData.pmtDpt, depassementTotal);
    }

    return {
      aEncaisser: encaisisserCalc,
      tiersPayant: tpCalc,
      hasChiffrage: rows.length > 1,
      brssTotalCalc: brssTotal,
      depassementTotalCalc: depassementTotal,
    };
  }, [rows, qualificationData, isNoteHono]);

  const pmtBRSS = String(qualificationData?.pmtBRSS ?? '').trim().toLowerCase();
  const pmtDpt = String(qualificationData?.pmtDpt ?? '').trim().toLowerCase();

  // Affichage modes de paiement : strict si patient
  const showPaymentModes = isPatient(pmtBRSS) || isPatient(pmtDpt);

  // Détection des tiers nécessitant des boutons (exclut 'patient', 'personne' et 'ssmut')
  const isPmtTiersPartenaire = (val) => {
    return !isPatient(val) && !isPersonne(val) && !isSSMut(val) && String(val ?? '').trim() !== '';
  };

  const isAnyTiersPartenaireActive = isPmtTiersPartenaire(pmtBRSS) || isPmtTiersPartenaire(pmtDpt);

  const { specialTPOptions, showSpecialTPSection } = useMemo(() => {
    if (!isAnyTiersPartenaireActive) {
      return { specialTPOptions: [], showSpecialTPSection: false };
    }

    const list = (qualificationData?.profileSpecialTPList || [])
      .filter((tp) => tp && String(tp).toLowerCase() !== 'aucun');

    // Vérifie s'il y a plus d'une correspondance distincte dans la config tiers
    const distinctTiersTargets = new Set();
    list.forEach((tp) => {
      const target = tiersMapping[tp] || String(tp).trim().replace(/\s+/g, '_');
      distinctTiersTargets.add(target);
    });

    return {
      specialTPOptions: list,
      showSpecialTPSection: distinctTiersTargets.size > 1,
    };
  }, [isAnyTiersPartenaireActive, qualificationData?.profileSpecialTPList, tiersMapping]);

  // Synchronisation avec filterTP
  useEffect(() => {
    if (showSpecialTPSection) {
      if (filterTP && specialTPOptions.includes(filterTP)) {
        setSelectedSpecialTP(filterTP);
      } else if (specialTPOptions.length === 1) {
        setSelectedSpecialTP(specialTPOptions[0]);
        setFilterTP(specialTPOptions[0]);
      }
    } else {
      setSelectedSpecialTP('');
    }
  }, [showSpecialTPSection, filterTP, specialTPOptions, setFilterTP]);

  const handleToggleSpecialTP = (tpName) => {
    if (selectedSpecialTP === tpName) {
      setSelectedSpecialTP('');
    } else {
      setSelectedSpecialTP(tpName);
      setFilterTP(tpName);
    }
  };

  const fillRestant = (fieldKey) => {
    if (!hasChiffrage) {
      toast.error("Veuillez d'abord récupérer le détail du chiffrage.");
      return;
    }
    const currentTpe = fieldKey === 'tpe' ? 0 : parsePayment(payments.tpe);
    const currentEspeces = fieldKey === 'especes' ? 0 : parsePayment(payments.especes);
    const currentSumup = fieldKey === 'sumup' ? 0 : parsePayment(payments.sumup);
    const currentVirement = (fieldKey === 'virement' || !virementManagerConfirmed) ? 0 : parsePayment(payments.virement);
    const currentCheque = (fieldKey === 'cheque' || !virementManagerConfirmed) ? 0 : parsePayment(payments.cheque);

    const otherTotal = currentTpe + currentEspeces + currentSumup + currentVirement + currentCheque;
    const restant = Math.max(0, parseFloat((aEncaisser - otherTotal).toFixed(2)));

    setPayments((prev) => ({
      ...prev,
      [fieldKey]: restant > 0 ? restant.toFixed(2) : '0.00',
    }));
  };

  const isPaymentValid = useMemo(() => {
    if (!hasChiffrage) return false;
    if (showSpecialTPSection && !selectedSpecialTP) return false;
    if (showPaymentModes) {
      return Math.abs(totalCollected - aEncaisser) < 0.01;
    }
    return true;
  }, [hasChiffrage, showSpecialTPSection, selectedSpecialTP, showPaymentModes, totalCollected, aEncaisser]);

  const handleClearChiffrage = () => {
    if (patient?.id) {
      setSheetMap((prev) => ({
        ...prev,
        [patient.id]: { table: [], updatedAt: null, banner: null },
      }));
    }
  };

  const handleCellCopy = async (val, label) => {
    const cleanVal = String(val ?? '').trim();
    if (!cleanVal) return;
    const ok = await copyToClipboard(cleanVal);
    if (ok) {
      toast.success(`${label || 'Valeur'} copié(e) : ${cleanVal}`);
    } else {
      toast.error('Impossible de copier');
    }
  };

  // Chargement de la config des tiers via l'API
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/passages/config`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const res = await r.json();
        if (mounted && res?.config?.tiers) {
          setTiersMapping(res.config.tiers);
        }
      } catch (e) {
        console.warn('Impossible de récupérer la config tiers :', e.message);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token]);

  // Chargement initial du passage
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/passages/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error('Chargement du patient impossible');
        const raw = await r.json();
        const p = mapDbPatient(raw);
        if (!mounted) return;
        setPatient(p);

        if (p.codes) setCodesMap((m) => ({ ...m, [p.id]: p.codes }));

        // 1. Déjà présent dans le modèle
        if (p.cotationSheet?.table?.length) {
          setSheetMap((m) => ({
            ...m,
            [p.id]: {
              table: p.cotationSheet.table,
              updatedAt: p.cotationSheet.updatedAt || null,
              rawPayload: p.cotationSheet,
            },
          }));
        } else {
          // 2. Récupération systématique du chiffrage déjà calculé
          const codes = getCodesStringFromPatient(p);
          if (codes) {
            const data = await sheetGet(p.id, token, domainOf(p));
            const normalized = normalizeSheetPayload(data) || {};
            const table = Array.isArray(normalized.table) ? normalized.table : [];
            const uAt =
              normalized.updatedAt ||
              normalized?.raw?.timestamp ||
              data?.raw?.timestamp ||
              null;
            const banner = normalized.banner ?? data?.banner ?? null;
            if (table.length) {
              setSheetMap((m) => ({
                ...m,
                [p.id]: { table, updatedAt: uAt, banner, rawPayload: data },
              }));
            }
          }
        }
      } catch (e) {
        setErrorMsg(e.message);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, token]);

  useEffect(() => {
    if (!socket) return;
    const onUpdate = (payload) => {
      if (String(payload.id) !== String(id)) return;
      const norm = mapDbPatient(payload);

      setPatient((prev) => {
        if (!prev) return norm;
        return {
          ...prev,
          ...norm,
          nom: norm.nom || prev.nom,
          prenom: norm.prenom || prev.prenom,
          civilite: norm.civilite || prev.civilite,
          dateNaissance: norm.dateNaissance || prev.dateNaissance,
          date_naissance: norm.date_naissance || prev.date_naissance,
          email: norm.email || prev.email,
        };
      });

      const wsTable = Array.isArray(norm?.cotationSheet?.table)
        ? norm.cotationSheet.table
        : Array.isArray(norm?.cotationSheet?.raw?.table)
        ? norm.cotationSheet.raw.table
        : [];

      if (wsTable.length) {
        const updatedAt =
          norm?.cotationSheet?.updatedAt ??
          norm?.cotationSheet?.raw?.updatedAt ??
          norm?.cotationSheet?.raw?.timestamp ??
          null;
        setSheetMap((m) => ({
          ...m,
          [norm.id]: { table: wsTable, updatedAt, rawPayload: norm?.cotationSheet },
        }));
      }
    };

    socket.on('patientUpdated', onUpdate);
    socket.on('patient_updated', onUpdate);
    return () => {
      socket.off('patientUpdated', onUpdate);
      socket.off('patient_updated', onUpdate);
    };
  }, [socket, id]);

  useEffect(() => {
    function onFinalized(e) {
      const { id: eId, codes } = e.detail || {};
      if (!eId || String(eId) !== String(id)) return;
      setCodesMap((m) => ({ ...m, [eId]: codes || '' }));
      setPatient((prev) =>
        prev
          ? { ...prev, currentstep: 8, currentStep: 8, cotationStatus: 'complete' }
          : prev
      );
    }
    window.addEventListener('musiq:cotation:finalized', onFinalized);
    return () => window.removeEventListener('musiq:cotation:finalized', onFinalized);
  }, [id]);

  async function fetchSheetFor(p, codes, qualifData) {
    const pid = p?.id;
    const cleanCodes = normalizeCodesApi(codes);
    if (!pid || !cleanCodes) return;

    const toastId = `sheet-${pid}`;
    const now = Date.now();

    try {
      toast('Calcul lancé…', { icon: '⏳', id: toastId });
      setSheetLoading(true);
      setSheetLoadingSince(now);

      let data;
      try {
        data = await sheetSyncAndPoll({
          passageId: pid,
          codes: cleanCodes,
          token,
          domain: domainOf(p),
          musiqId: getDisplayMusiqId(p),
          isDemo: p.is_demo,
          resQualif: qualifData,
        });
      } catch (e) {
        const msg = String(e?.message || e);
        if (/duplicate|conflict|409/i.test(msg)) {
          data = await sheetGet(pid, token, domainOf(p));
        } else {
          throw e;
        }
      }

      const normalized = normalizeSheetPayload(data) || { table: [], updatedAt: null };
      const incomingTable = Array.isArray(normalized.table) ? normalized.table : [];
      const incomingUpdatedAt = normalized.updatedAt || normalized.timestamp || null;
      const incomingBanner = normalized.banner ?? data?.banner ?? null;

      setSheetMap((prev) => ({
        ...prev,
        [pid]: {
          table: incomingTable,
          updatedAt: incomingUpdatedAt,
          banner: incomingBanner,
          rawPayload: data,
        },
      }));

      if (incomingTable.length) {
        toast.success('Détail cotation récupéré', { id: toastId });
      } else {
        toast('Toujours en cours…', { id: toastId });
      }
    } catch (e) {
      console.error(e);
      toast.error('Échec de la récupération depuis la feuille');
    } finally {
      setSheetLoading(false);
      setSheetLoadingSince(null);
    }
  }

  async function handleSavePayments() {
    try {
      setIsSavingPayment(true);

      const timestampValidation = new Date().toISOString();
      const musiqIdReadable = getDisplayMusiqId(patient);

      const actesDetails = [];
      if (rows.length > 1) {
        rows.slice(1).forEach((r) => {
          let code = '';
          let montantRaw = 0;

          if (Array.isArray(r)) {
            code = String(r[0] ?? '').trim();
            montantRaw = r[1];
          } else if (r && typeof r === 'object') {
            const vals = Object.values(r);
            code = String(vals[0] ?? '').trim();
            montantRaw = vals[1];
          }

          const isTotal = /^total/i.test(code);
          if (!isTotal && code) {
            actesDetails.push({
              code,
              montant: parseNum(montantRaw),
            });
          }
        });
      }

      const payload = {
        nom: patient?.nom || '',
        prenom: patient?.prenom || '',
        id_musiq: musiqIdReadable,
        validated_at: timestampValidation,
        payments: {
          tpe: showPaymentModes ? parsePayment(payments.tpe) : 0,
          especes: showPaymentModes ? parsePayment(payments.especes) : 0,
          virement: showPaymentModes && virementManagerConfirmed ? parsePayment(payments.virement) : 0,
          cheque: showPaymentModes && virementManagerConfirmed ? parsePayment(payments.cheque) : 0,
          sumup: showPaymentModes ? parsePayment(payments.sumup) : 0,
        },
        qualificationData: {
          typeFact: qualificationData?.typeFact || '',
          pmtBRSS: qualificationData?.pmtBRSS || '',
          pmtDpt: qualificationData?.pmtDpt || '',
        },
        brssTotal: brssTotalCalc,
        depassementTotal: depassementTotalCalc,
        specialTP: showSpecialTPSection ? selectedSpecialTP : (filterTP !== 'Aucun' ? filterTP : null),
        actesDetails,
      };

      const res = await fetch(`${API_URL}/passages/${patient.id}/paiement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error("Erreur serveur lors de l'enregistrement");
      }

      toast.success('Encaissement enregistré avec succès');
    } catch (e) {
      console.error('❌ Erreur validation paiement :', e);
      toast.error("Échec de l'enregistrement de l'encaissement");
    } finally {
      setIsSavingPayment(false);
    }
  }

  const [, setTick] = useState(0);
  useEffect(() => {
    if (!sheetLoading) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [sheetLoading]);

  if (!patient) {
    return (
      <PageLayout title="Détails Cotation">
        <div className="p-6">{errorMsg ? `Erreur : ${errorMsg}` : 'Chargement…'}</div>
      </PageLayout>
    );
  }

  const p = patient;
  const rawCodes =
    codesMap[p.id] ||
    p.codes ||
    p.cotationCodes ||
    p.cotation_codes ||
    getCodesStringFromPatient(p) ||
    '';
  const codes = String(rawCodes || '').toUpperCase().replace(/\s*\+\s*/g, '+');

  const elapsed =
    sheetLoading && sheetLoadingSince
      ? Math.max(0, Math.floor((Date.now() - sheetLoadingSince) / 1000))
      : 0;
  const updatedAt = sheetMap[p.id]?.updatedAt || null;
  const codesClean = normalizeCodesLocal(rawCodes);

  const sheetMsg = sheetMap[p.id]?.banner ?? sheetMap[p.id]?.message ?? null;
  const currentSheetJson = sheetMap[p.id]?.rawPayload || sheetMap[p.id]?.table || null;

  const patientTitle =
    `${p?.civilite || ''} ${p?.prenom || ''} ${p?.nom?.toUpperCase?.()}`.trim() || 'Sortie';

  return (
    <PageLayout
      title={patientTitle}
      navProps={{
        left: { label: 'Retour', action: () => navigate(-1) },
      }}
    >
      <div className="px-2 sm:px-5 py-3 space-y-4">
        {/* Bandeau infos patient */}
        <div className="rounded-2xl bg-gradient-to-r from-[#B3000A] to-[#E1141C] text-white px-4 py-3 sm:px-5 sm:py-3.5 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 sm:gap-4">
          <div className="flex items-center gap-3">
            <div className="text-base sm:text-lg font-bold tracking-tight">
              {p?.civilite ? `${p.civilite} ` : ''}
              {String(p?.nom || '').toUpperCase()} {p?.prenom || ''}
            </div>

            {isAdmin && (
              <button
                type="button"
                onClick={() => setIsDebugOpen((prev) => !prev)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-mono font-bold transition-all cursor-pointer ${
                  isDebugOpen
                    ? 'bg-amber-400 text-slate-900 shadow-inner translate-y-[1px] ring-2 ring-amber-300'
                    : 'bg-black/25 text-white hover:bg-black/40 border border-white/20'
                }`}
                title="Afficher/masquer les JSON de debug"
              >
                <Bug className="h-3.5 w-3.5" />
                <span>Debug</span>
              </button>
            )}
          </div>

          <div className="text-xs sm:text-sm text-white/90 flex items-center gap-2 sm:justify-end">
            <span>
              Né(e) le{' '}
              {p?.dateNaissance
                ? new Date(p.dateNaissance).toLocaleDateString('fr-FR')
                : '—'}
            </span>
            {getUid(p) && (
              <>
                <span>•</span>
                <span className="font-mono text-xs tracking-wider text-white/90">
                  {getUid(p)}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Panneau de qualification */}
        <QualificationPanel
          onQualificationChange={setQualificationData}
          onClearChiffrage={handleClearChiffrage}
          filterTP={filterTP}
          setFilterTP={setFilterTP}
          etablissement={etablissement || patient?.etablissement}
        />

        {/* Section Debug (accessible uniquement aux admins) */}
        {isAdmin && isDebugOpen && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50/70 p-4 shadow-sm space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-amber-200">
              <Bug className="h-4 w-4 text-amber-700" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-amber-900">
                Panneau de débogage administrateur
              </h3>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-bold text-slate-700 mb-1">
                  1. Objet ResQualif :
                </div>
                <pre className="p-3 bg-slate-900 text-emerald-400 rounded-xl text-xs font-mono overflow-x-auto max-h-72 border border-slate-800">
                  {JSON.stringify(qualificationData, null, 2)}
                </pre>
              </div>

              <div>
                <div className="text-xs font-bold text-slate-700 mb-1">
                  2. JSON Chiffrage (issu de l'API / Sheets) :
                </div>
                <pre className="p-3 bg-slate-900 text-cyan-400 rounded-xl text-xs font-mono overflow-x-auto max-h-72 border border-slate-800">
                  {currentSheetJson
                    ? JSON.stringify(currentSheetJson, null, 2)
                    : '// Aucun chiffrage chargé pour ce patient'}
                </pre>
              </div>
            </div>
          </div>
        )}

        {/* Bloc Cotation / Chiffrage */}
        <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-5 shadow-sm space-y-3 border-l-4 border-l-[#B3000A]">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
            <h2 className="text-base sm:text-lg font-semibold text-slate-800">
              Détails du chiffrage
            </h2>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (sheetLoading || isCotationLibre(p)) return;
                  if (isFetchDisabled) {
                    if (qualificationData?.isInvalid) {
                      toast.error('Combinaison de qualification invalide.');
                    } else if (qualificationData?.requiresCheck && !qualificationData?.infoConfirmed) {
                      toast.error('Veuillez cocher la case de confirmation des justificatifs.');
                    }
                    return;
                  }
                  fetchSheetFor(p, codesClean, qualificationData);
                }}
                className={[
                  'px-3 py-1.5 rounded-md inline-flex items-center gap-1 text-xs sm:text-sm border transition-colors',
                  sheetLoading || isCotationLibre(p) || isFetchDisabled
                    ? 'opacity-50 cursor-pointer bg-slate-100 border-slate-200 text-slate-400'
                    : 'hover:bg-slate-50 border-slate-300 text-slate-700',
                ].join(' ')}
              >
                {sheetLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Calcul… {String(elapsed).padStart(2, '0')} s</span>
                  </>
                ) : (
                  <>
                    <RefreshCcw className="h-4 w-4" />
                    <span>{rows.length ? 'Relancer le calcul' : 'Récupérer le détail'}</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => !sheetLoading && setSearchParams({ cotation: p.id })}
                disabled={!!sheetLoading}
                className={[
                  'px-3 py-1.5 rounded-md border text-xs sm:text-sm font-medium',
                  sheetLoading
                    ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-slate-900 text-white hover:bg-slate-800',
                ].join(' ')}
              >
                {codesClean ? 'Modifier la cotation' : 'Commencer la cotation'}
              </button>
            </div>
          </div>

          {/* Cotation libre */}
          {isCotationLibre(p) && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] text-slate-600 uppercase tracking-wide mb-1 font-semibold">
                Cotation libre
              </div>
              <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                {getCotationLibreText(p)}
              </div>
            </div>
          )}

          {/* Codes générés et Message Sheet : s'affiche systématiquement dès que les codes existent */}
          {((codes && !isCotationLibre(p)) || sheetMsg) && (
            <div className="flex flex-col sm:flex-row gap-2 items-stretch">
              {codes && !isCotationLibre(p) && (
                <div className="flex-1 min-w-0 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 font-mono text-xs sm:text-sm text-rose-800 flex items-center justify-between gap-2">
                  <span className="truncate">{codes}</span>
                  <button
                    type="button"
                    onClick={async () => {
                      const textToCopy = String(codes).replace(/\s*\+\s*/g, '+');
                      const ok = await copyToClipboard(textToCopy);
                      if (ok) {
                        toast.success('Codes copiés');
                      } else {
                        toast.error('Impossible de copier');
                      }
                    }}
                    className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-white border text-xs hover:bg-rose-100 border-rose-200 text-rose-800 transition"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    <span>Copier</span>
                  </button>
                </div>
              )}

              {sheetMsg && (
                <div className="flex-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs sm:text-sm text-amber-900 flex items-center">
                  <span>{sheetMsg}</span>
                </div>
              )}
            </div>
          )}

          {/* Tableau détaillé */}
          {!!rows.length && (
            <div className="border rounded-lg mt-2 overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {Array.isArray(rows[0])
                      ? rows[0].map((h, i) => (
                          <th
                            key={i}
                            className={[
                              'px-3 py-2 font-semibold text-slate-700',
                              i === 2 || i === 3 ? 'text-right' : 'text-left',
                            ].join(' ')}
                          >
                            {String(h ?? '')}
                          </th>
                        ))
                      : Object.keys(rows[0]).map((k) => (
                          <th key={k} className="px-3 py-2 text-left font-semibold text-slate-700">
                            {k}
                          </th>
                        ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {Array.isArray(rows[0])
                    ? rows.slice(1).map((r, ri) => {
                        const isTotal = /^total/i.test(String(r?.[0] ?? ''));
                        return (
                          <tr key={ri} className={isTotal ? 'bg-amber-50/50 font-semibold' : 'bg-white'}>
                            {r.map((c, ci) => {
                              const val = String(c ?? '');
                              const isNum = /^-?\d+(?:[.,]\d+)?$/.test(val.trim());
                              const isClickable = !isTotal && (
                                isNoteHono
                                  ? ci === 0 || ci === 1
                                  : ci === 1 || ci === 2
                              );

                              return (
                                <td
                                  key={ci}
                                  onClick={() => isClickable && handleCellCopy(val, isNoteHono ? (ci === 0 ? 'Code' : 'Montant') : (ci === 1 ? 'Code' : 'Montant Doctolib'))}
                                  title={isClickable ? 'Cliquer pour copier' : undefined}
                                  className={[
                                    'px-3 py-2 align-middle text-slate-800 transition-colors group relative',
                                    ci === 2 || ci === 3 ? 'text-right' : 'text-left',
                                    isClickable ? 'cursor-pointer hover:bg-rose-100 hover:text-[#B3000A] font-medium' : '',
                                  ].join(' ')}
                                >
                                  <div className={`inline-flex items-center gap-1.5 ${ci === 2 || ci === 3 ? 'justify-end w-full' : ''}`}>
                                    <span>{isNum ? `${val.replace('.', ',')} €` : val}</span>
                                    {isClickable && (
                                      <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100 text-[#B3000A] transition-opacity shrink-0" />
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    : rows.map((r, ri) => {
                        const keys = Object.keys(rows[0]);
                        const firstKey = keys[0];
                        const isTotal =
                          keys.some((k) => /total/i.test(k)) ||
                          /^total/i.test(String(r?.[firstKey] ?? ''));
                        return (
                          <tr key={ri} className={isTotal ? 'bg-amber-50/50 font-semibold' : 'bg-white'}>
                            {keys.map((k, ci) => {
                              const raw = String(r?.[k] ?? '');
                              const isNum = /^-?\d+(?:[.,]\d+)?$/.test(raw.trim());
                              const isClickable = !isTotal && (
                                isNoteHono
                                  ? ci === 0 || ci === 1
                                  : ci === 1 || ci === 2
                              );

                              return (
                                <td
                                  key={k}
                                  onClick={() => isClickable && handleCellCopy(raw, isNoteHono ? (ci === 0 ? 'Code' : 'Montant') : (ci === 1 ? 'Code' : 'Montant Doctolib'))}
                                  title={isClickable ? 'Cliquer pour copier' : undefined}
                                  className={[
                                    'px-3 py-2 align-middle text-slate-800 transition-colors group relative',
                                    /^(brss|montant)/i.test(k) ? 'text-right' : 'text-left',
                                    isClickable ? 'cursor-pointer hover:bg-rose-100 hover:text-[#B3000A] font-medium' : '',
                                  ].join(' ')}
                                >
                                  <div className={`inline-flex items-center gap-1.5 ${/^(brss|montant)/i.test(k) ? 'justify-end w-full' : ''}`}>
                                    <span>{isNum ? `${raw.replace('.', ',')} €` : raw}</span>
                                    {isClickable && (
                                      <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100 text-[#B3000A] transition-opacity shrink-0" />
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          )}

          {!!rows.length && (
            <div className="text-[11px] text-slate-400 text-right mt-1">
              Prêt{updatedAt ? ` — récupéré le ${fmtDT(updatedAt)}` : ''}
            </div>
          )}
        </div>

        {/* Bloc Encaissement */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm space-y-4 border-l-4 border-l-emerald-500">
          <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
            <Banknote className="h-5 w-5 text-emerald-600" />
            <h2 className="text-base sm:text-lg font-semibold text-slate-800">
              Collecté par le secrétariat
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            {/* Boutons Tiers payant spécial si > 1 correspondance dans tiers */}
            {showSpecialTPSection &&
              specialTPOptions.map((tpName) => {
                const isSelected = selectedSpecialTP === tpName;
                return (
                  <div key={tpName} className="flex flex-col justify-end">
                    <button
                      type="button"
                      onClick={() => handleToggleSpecialTP(tpName)}
                      className={`w-full inline-flex items-center justify-center gap-2 text-xs font-semibold py-2.5 px-3 rounded-lg border transition-all h-[38px] cursor-pointer select-none active:scale-95 ${
                        isSelected
                          ? 'bg-emerald-600 text-white border-emerald-600 shadow-inner ring-2 ring-emerald-400 ring-offset-1 translate-y-[1px]'
                          : 'bg-slate-50 text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900 shadow-2xs'
                      }`}
                    >
                      <Building2
                        className={`h-4 w-4 ${
                          isSelected ? 'text-white' : 'text-slate-500'
                        }`}
                      />
                      <span className="truncate">{tpName}</span>
                    </button>
                  </div>
                );
              })}

            {/* Saisie des modes de règlement (patient) */}
            {showPaymentModes && (
              <>
                <div className="flex flex-col">
                  <label
                    onClick={() => fillRestant('tpe')}
                    title="Cliquer pour remplir le solde restant à encaisser"
                    className="w-fit text-xs font-semibold px-2 py-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 mb-1.5 flex items-center gap-1.5 cursor-pointer transition-all hover:bg-rose-100 hover:text-[#B3000A] hover:border-rose-200 select-none shadow-2xs"
                  >
                    <CreditCard className="h-3.5 w-3.5 text-slate-400" />
                    <span>TPE</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={payments.tpe}
                      onChange={(e) => setPayments({ ...payments, tpe: e.target.value })}
                      placeholder="0.00"
                      className="w-full rounded-lg border border-slate-300 py-2 pl-3 pr-8 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all"
                    />
                    <span className="absolute right-3 top-2 text-slate-400 text-sm font-medium">€</span>
                  </div>
                </div>

                <div className="flex flex-col">
                  <label
                    onClick={() => fillRestant('especes')}
                    title="Cliquer pour remplir le solde restant à encaisser"
                    className="w-fit text-xs font-semibold px-2 py-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 mb-1.5 flex items-center gap-1.5 cursor-pointer transition-all hover:bg-rose-100 hover:text-[#B3000A] hover:border-rose-200 select-none shadow-2xs"
                  >
                    <Banknote className="h-3.5 w-3.5 text-slate-400" />
                    <span>Espèces</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={payments.especes}
                      onChange={(e) => setPayments({ ...payments, chips: undefined, especes: e.target.value })}
                      placeholder="0.00"
                      className="w-full rounded-lg border border-slate-300 py-2 pl-3 pr-8 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all"
                    />
                    <span className="absolute right-3 top-2 text-slate-400 text-sm font-medium">€</span>
                  </div>
                </div>

                <div className="flex flex-col">
                  <label
                    onClick={() => fillRestant('sumup')}
                    title="Cliquer pour remplir le solde restant à encaisser"
                    className="w-fit text-xs font-semibold px-2 py-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 mb-1.5 flex items-center gap-1.5 cursor-pointer transition-all hover:bg-rose-100 hover:text-[#B3000A] hover:border-rose-200 select-none shadow-2xs"
                  >
                    <Smartphone className="h-3.5 w-3.5 text-slate-400" />
                    <span>SumUp</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={payments.sumup}
                      onChange={(e) => setPayments({ ...payments, sumup: e.target.value })}
                      placeholder="0.00"
                      className="w-full rounded-lg border border-slate-300 py-2 pl-3 pr-8 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all"
                    />
                    <span className="absolute right-3 top-2 text-slate-400 text-sm font-medium">€</span>
                  </div>
                </div>

                {!showVirementCheque && (
                  <div className="flex flex-col justify-end">
                    <button
                      type="button"
                      onClick={() => setShowVirementCheque(true)}
                      className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 border border-slate-300 bg-slate-50 hover:bg-slate-100 py-2.5 px-3 rounded-lg transition-colors h-[38px] cursor-pointer"
                    >
                      <Landmark className="h-3.5 w-3.5 text-slate-500" />
                      Virement ou chèque
                    </button>
                  </div>
                )}

                {showVirementCheque && virementManagerConfirmed && (
                  <>
                    <div className="flex flex-col">
                      <label
                        onClick={() => fillRestant('virement')}
                        title="Cliquer pour remplir le solde restant à encaisser"
                        className="w-fit text-xs font-semibold px-2 py-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 mb-1.5 flex items-center gap-1.5 cursor-pointer transition-all hover:bg-rose-100 hover:text-[#B3000A] hover:border-rose-200 select-none shadow-2xs"
                      >
                        <Landmark className="h-3.5 w-3.5 text-slate-400" />
                        <span>Virement</span>
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={payments.virement}
                          onChange={(e) => setPayments({ ...payments, virement: e.target.value })}
                          placeholder="0.00"
                          className="w-full rounded-lg border border-slate-300 py-2 pl-3 pr-8 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all"
                        />
                        <span className="absolute right-3 top-2 text-slate-400 text-sm font-medium">€</span>
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <label
                        onClick={() => fillRestant('cheque')}
                        title="Cliquer pour remplir le solde restant à encaisser"
                        className="w-fit text-xs font-semibold px-2 py-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 mb-1.5 flex items-center gap-1.5 cursor-pointer transition-all hover:bg-rose-100 hover:text-[#B3000A] hover:border-rose-200 select-none shadow-2xs"
                      >
                        <CheckSquare className="h-3.5 w-3.5 text-slate-400" />
                        <span>Chèque</span>
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={payments.cheque}
                          onChange={(e) => setPayments({ ...payments, cheque: e.target.value })}
                          placeholder="0.00"
                          className="w-full rounded-lg border border-slate-300 py-2 pl-3 pr-8 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all"
                        />
                        <span className="absolute right-3 top-2 text-slate-400 text-sm font-medium">€</span>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {showPaymentModes && showVirementCheque && (
            <div className="pt-2">
              <label className="flex items-center gap-3 p-3.5 rounded-xl border border-[#FDE68A] bg-[#FEF9C3]/70 text-[#713F12] text-xs sm:text-sm font-medium cursor-pointer select-none transition-colors hover:bg-[#FEF08A]/70">
                <input
                  type="checkbox"
                  checked={virementManagerConfirmed}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setVirementManagerConfirmed(checked);
                    if (!checked) {
                      setShowVirementCheque(false);
                      setPayments((prev) => ({ ...prev, virement: '', cheque: '' }));
                    }
                  }}
                  className="h-5 w-5 rounded border-slate-400 text-[#B3000A] focus:ring-[#B3000A] cursor-pointer"
                />
                <span className="leading-snug">Mode de paiement validé par un manager</span>
              </label>
            </div>
          )}

          {/* Totaux & Bouton de validation */}
          <div className="flex flex-col sm:flex-row items-center justify-between pt-3 border-t border-slate-100 mt-2 gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <div
                className={`text-base font-semibold px-4 py-2 rounded-lg border transition-colors ${
                  hasChiffrage
                    ? 'bg-slate-50 border-slate-200 text-slate-700'
                    : 'bg-slate-100 border-slate-200 text-slate-400'
                }`}
              >
                À encaisser :{' '}
                <span
                  className={`text-lg ml-1 font-bold ${
                    hasChiffrage ? 'text-emerald-700' : 'text-slate-400'
                  }`}
                >
                  {hasChiffrage ? `${aEncaisser.toFixed(2)} €` : '— €'}
                </span>
              </div>

              <div
                className={`text-base font-semibold px-4 py-2 rounded-lg border transition-colors ${
                  hasChiffrage
                    ? 'bg-slate-50 border-slate-200 text-slate-700'
                    : 'bg-slate-100 border-slate-200 text-slate-400'
                }`}
              >
                Tiers payant :{' '}
                <span
                  className={`text-lg ml-1 font-bold ${
                    hasChiffrage ? 'text-blue-700' : 'text-slate-400'
                  }`}
                >
                  {hasChiffrage ? `${tiersPayant.toFixed(2)} €` : '— €'}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                if (isSavingPayment) return;

                if (!hasChiffrage) {
                  toast.error("Veuillez récupérer le détail de la cotation avant de valider l'encaissement.");
                  return;
                }

                if (showSpecialTPSection && !selectedSpecialTP) {
                  toast.error('Veuillez sélectionner un partenaire avant de valider.');
                  return;
                }

                if (showPaymentModes && Math.abs(totalCollected - aEncaisser) >= 0.01) {
                  toast.error(
                    `Le total collecté (${totalCollected.toFixed(2)} €) ne correspond pas au montant à encaisser (${aEncaisser.toFixed(2)} €).`
                  );
                  return;
                }

                handleSavePayments();
              }}
              className={[
                'w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all shadow-sm',
                !isPaymentValid || isSavingPayment
                  ? 'bg-slate-200 text-slate-400 border border-slate-300 cursor-not-allowed opacity-80'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer',
              ].join(' ')}
            >
              {isSavingPayment ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Save className="h-5 w-5" />
              )}
              Valider l'encaissement
            </button>
          </div>
        </div>
      </div>

      <CotationOverlay
        open={!!overlayId}
        passageId={overlayId}
        onClose={() => setSearchParams({})}
        patients={patients}
        setPatients={setPatients}
      />
    </PageLayout>
  );
}